import React from 'react';
import './TimelineBar.css';
import { ClassroomRatioTimeSegment } from '../../../../server/src/types/classroom';

import { Child, TeacherInfo } from '../../types';

interface TimelineBarProps {
  segments: ClassroomRatioTimeSegment[];
  timeRange: { start: Date; end: Date };
  labelFormatter?: (segment: ClassroomRatioTimeSegment) => React.ReactNode;
  className?: string;
  teachers: TeacherInfo[];
  students: Child[];
  requiredRatio: number;
}

const TimelineBar: React.FC<TimelineBarProps> = ({
  segments,
  timeRange,
  labelFormatter,
  className = '',
  teachers,
  students ,
  requiredRatio
}) => {
  // Calculate the time span based on the current timeRange
    const startHour = timeRange.start.getHours();
    const endHour = timeRange.end.getHours();
    const totalHours = endHour - startHour;
    const totalMinutes = totalHours * 60;

    const ratioClassMapping = (segment:ClassroomRatioTimeSegment) => {
        // Calculate color based on the ratio
        const requiredTeachers = Math.ceil(segment.studentIds.length / requiredRatio);
        const presentTeachers = segment.activeTeacherIds.length;
        
        if (presentTeachers === 0 && segment.studentIds.length === 0) return 'empty';
        if (presentTeachers === 0 && segment.studentIds.length > 0) return 'under';
        
        return presentTeachers > requiredTeachers ? 'over' : 
              presentTeachers < requiredTeachers ? 'under' : 'optimal';
    }

    const tooltipFormatter = (segment:ClassroomRatioTimeSegment) => {
        return `Time: ${segment.startTime.toLocaleTimeString()} - ${segment.endTime.toLocaleTimeString()}, 
Section Students: ${segment.studentIds.length}, 
Section Active Teachers: ${segment.activeTeacherIds.length}/${segment.activeTeacherIds.length + segment.teachersOnBreakIds.length}, 
Section Ratio: ${(segment.studentIds.length/(segment.activeTeacherIds.length || 1)).toFixed(1)}
Teachers: ${segment.activeTeacherIds.map(id => {
const teacher = teachers.find(teach => teach.guid == id)
return teacher ? `${teacher.firstName} ${teacher.lastName}`  : id;
}).join(', ')},
Teachers On Break: ${segment.teachersOnBreakIds.map(id => {
const teacher = teachers.find(teach => teach.guid == id)
return teacher ? `${teacher.firstName} ${teacher.lastName}`  : id;
}).join(', ')}`
    }


    return (
        <div className={`time-bars ${className}`}>
        {segments.map((segment, index) => {
            // Ensure dates are properly parsed
            const startDate = typeof segment.startTime === 'string' 
            ? new Date(segment.startTime) 
            : segment.startTime;
            const endDate = typeof segment.endTime === 'string'
            ? new Date(segment.endTime)
            : segment.endTime;
            
            // Calculate start and end percentages based on current timeRange
            const startHour = startDate.getHours();
            const startMinute = startDate.getMinutes();
            const endHour = endDate.getHours();
            const endMinute = endDate.getMinutes();
            
            const startMinutesSinceRangeStart = (startHour - timeRange.start.getHours()) * 60 + startMinute;
            const endMinutesSinceRangeStart = (endHour - timeRange.start.getHours()) * 60 + endMinute;
            
            const startPercent = Math.max(0, startMinutesSinceRangeStart / totalMinutes);
            const endPercent = Math.min(1, endMinutesSinceRangeStart / totalMinutes);
            const widthPercent = (endPercent - startPercent) * 100;
            
            // Get color for this segment
            const ratioClass = ratioClassMapping(segment);
            
            // Prepare style based on positioning strategy
            const style ={
                left: `${startPercent * 100}%`,
                width: `${widthPercent}%`,

                };
            
            return (
            <div
                key={index}
                className={`time-bar ratio-${ratioClass}`}
                style={style}
                title={tooltipFormatter(segment)}
            >
                {labelFormatter && labelFormatter(segment)}
            </div>
            );
        })}
        </div>
    );
};

export default TimelineBar;
